// ─────────────────────────────────────────────────────────────────
// Email variables
// ----------------------------------------------------------------
// Definitions are stored on the email row and validated by
// VariableDefinitionsSchema (packages/shared/src/variable_definitions.ts).
// The checks below mirror that schema so mistakes surface in the panel
// instead of coming back as a 422 from PUT /api/emails/:id/variables.
// ─────────────────────────────────────────────────────────────────
import {
  parseVariableDefinitions,
  VARIABLE_NAME,
  type VariableDefinition,
  type VariableDefinitions,
} from '@blogforge/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../hooks/api';
import { BLOGFORGER_QR_DATA_URL } from '../../lib/constants';
import { Validators } from '../../lib/validators';

type VariableType = VariableDefinition['type'];
/** A list variable, which is a container rather than a value. */
type ListDefinition = Extract<VariableDefinition, { type: 'list' }>;
/** The variables one item of a list carries. */
type ListVars = NonNullable<ListDefinition['vars']>;

/** Type picker order — mirrors the variants of VariableDefinitionSchema. */
const VARIABLE_TYPES: VariableType[] = [
  'string',
  'url',
  'data_image_url',
  'number',
  'boolean',
  'enum',
  'array',
  'list',
];

/**
 * What a list may hold. The schema builds a list out of the variants that carry
 * a value, so the child picker stops where a list would begin — no nesting.
 */
const CHILD_VARIABLE_TYPES = VARIABLE_TYPES.filter((type) => type !== 'list');

const VARIABLE_TYPE_LABELS: Record<VariableType, string> = {
  string: 'Text',
  number: 'Number',
  boolean: 'Yes / no',
  enum: 'Choice',
  array: 'List',
  url: 'Url',
  data_image_url: 'Data Image',
  list: 'Variable List',
};

function readItems(value: string): string[] {
  const items = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return [...new Set(items)];
}

function variableNameProblem(name: string, alreadyExists: boolean): string | null {
  if (!name) return 'Give the variable a name.';
  if (name === '__proto__') return 'The name __proto__ is reserved.';
  if (!VARIABLE_NAME.test(name)) {
    return 'Names use letters, digits and underscores, and cannot start with a digit.';
  }
  if (alreadyExists) return `A variable named ${name} already exists.`;
  return null;
}

/** Drops `default` entirely — the schema treats it as optional, and `undefined` is not allowed. */
function withoutDefault(definition: VariableDefinition): VariableDefinition {
  const { default: _default, ...rest } = definition;
  return rest;
}

function withoutDescription(definition: VariableDefinition): VariableDefinition {
  const { description: _description, ...rest } = definition;
  return rest;
}

/** Rebuilds a definition for a new type, since each variant carries its own default. */
function reseedDefinition(definition: VariableDefinition, type: VariableType): VariableDefinition {
  const base: { required: boolean; description?: string } = { required: definition.required };
  if (definition.description) base.description = definition.description;

  switch (type) {
    case 'string':
    case 'url':
    case 'data_image_url':
      return { ...base, type, default: '' };
    case 'number':
      return { ...base, type: 'number', default: 0 };
    case 'boolean':
      return { ...base, type: 'boolean', default: false };
    case 'enum': {
      const options = definition.type === 'enum' ? definition.enum : [];
      const first = options[0];
      return first === undefined
        ? { ...base, type: 'enum', enum: options }
        : { ...base, type: 'enum', enum: options, default: first };
    }
    case 'array':
      return { ...base, type: 'array', default: [] };
    case 'list':
      // A list is a container: it starts with no children, and so with no
      // default either — the default is derived from the children (see
      // `withListVars`).
      return { ...base, type: 'list', vars: {} };
  }
}

/**
 * The default a list carries: one item whose values are its children's own
 * defaults, which is what the editor renders the item template with. `null`
 * when no child has a default, which leaves the list without one.
 */
function listDefaultFromVars(vars: ListVars): unknown[] | null {
  const item: Record<string, unknown> = {};
  for (const [name, child] of Object.entries(vars)) {
    if (child.default !== undefined) item[name] = child.default;
  }
  return Object.keys(item).length === 0 ? null : [item];
}

/** Rebuilds a list around a new set of children, keeping its default in step. */
function withListVars(list: ListDefinition, vars: ListVars): VariableDefinition {
  const next: ListDefinition = { ...list, vars };
  const derived = listDefaultFromVars(vars);
  return derived === null ? withoutDefault(next) : { ...next, default: derived };
}

/** Keeps an enum's default pointing at one of its choices. */
function withEnumOptions(
  definition: Extract<VariableDefinition, { type: 'enum' }>,
  options: string[],
): VariableDefinition {
  const fallback =
    definition.default !== undefined && options.includes(definition.default)
      ? definition.default
      : options[0];
  const next: VariableDefinition = { ...definition, enum: options };
  return fallback === undefined ? withoutDefault(next) : { ...next, default: fallback };
}

/**
 * Content snapshot of one definition, independent of key order — Postgres
 * hands jsonb back with its own ordering, so comparing raw JSON would report
 * a change after every save.
 */
function definitionSnapshot(definition: VariableDefinition): unknown[] {
  return [
    definition.type,
    definition.required,
    definition.description ?? null,
    definition.type === 'enum' ? definition.enum : null,
    definition.type === 'list' ? listVarsSnapshot(definition.vars) : null,
    defaultSnapshot(definition),
  ];
}

/**
 * A default as content. `default` is jsonb too, and a list's default is an item
 * object — whose keys Postgres hands back in its own order — so an item's keys
 * are read in name order for the same reason the record's own keys are.
 */
function defaultSnapshot(definition: VariableDefinition): unknown {
  const value = definition.default;
  if (value === undefined) return null;
  if (definition.type !== 'list' || !Array.isArray(value)) return value;
  return value.map((item) =>
    item && typeof item === 'object' && !Array.isArray(item)
      ? Object.entries(item as Record<string, unknown>).sort(([a], [b]) =>
          a < b ? -1 : a > b ? 1 : 0,
        )
      : item,
  );
}

/** A list's children as content: `vars` is jsonb too, so it is read in name order. */
function listVarsSnapshot(vars: ListVars | undefined): unknown {
  if (!vars) return null;
  return Object.keys(vars)
    .sort()
    .map((name) => [name, definitionSnapshot(vars[name]!)]);
}

function definitionsSnapshot(definitions: VariableDefinitions): string {
  return JSON.stringify(
    Object.keys(definitions)
      .sort()
      .map((name) => [name, definitionSnapshot(definitions[name] as VariableDefinition)]),
  );
}

/** One definition's own constraints, reported under the label the panel shows. */
function definitionProblem(definition: VariableDefinition, label: string): string | null {
  if (definition.type === 'enum') {
    if (definition.enum.length === 0) return `${label} needs at least one choice.`;
    if (definition.default !== undefined && !definition.enum.includes(definition.default)) {
      return `The default for ${label} must be one of its choices.`;
    }
  }
  if (definition.type === 'number' && definition.default !== undefined) {
    if (!Number.isFinite(definition.default)) return `The default for ${label} must be a number.`;
  }
  return null;
}

/** Client-side mirror of the schema's constraints. */
function definitionsProblem(definitions: VariableDefinitions): string | null {
  for (const [name, definition] of Object.entries(definitions)) {
    const nameProblem = variableNameProblem(name, false);
    if (nameProblem) return nameProblem;
    const own = definitionProblem(definition, `"${name}"`);
    if (own) return own;

    // A list's children carry the same constraints one level down, so they are
    // checked here too rather than coming back as a 422 naming a child.
    if (definition.type !== 'list') continue;
    for (const [childName, child] of Object.entries(definition.vars ?? {})) {
      const childProblem =
        variableNameProblem(childName, false) ??
        definitionProblem(child, `"${childName}" in "${name}"`);
      if (childProblem) return childProblem;
    }
  }
  return null;
}

/** The variables route answers with a zod issue array — surface the first issue. */
function variablesErrorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    const detail: unknown = cause.body?.['error'];
    if (Array.isArray(detail) && detail.length > 0) {
      const [first] = detail as unknown[];
      if (first && typeof first === 'object') {
        const issue = first as { message?: unknown; path?: unknown };
        const path =
          Array.isArray(issue.path) && issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
        return `${path}${typeof issue.message === 'string' ? issue.message : 'Invalid definition'}`;
      }
    }
    return cause.message;
  }
  return cause instanceof Error ? cause.message : 'Could not save variables.';
}

interface NewVariableForm {
  name: string;
  type: VariableType;
  required: boolean;
  /** Comma-separated choices (`enum`) or items (`array`). */
  items: string;
  /** Default text for `string` / `number`. */
  value: string;
}

const EMPTY_VARIABLE_FORM: NewVariableForm = {
  name: '',
  type: 'string',
  required: false,
  items: '',
  value: '',
};

/** The checks a draft must pass before it becomes a definition. */
function draftProblem(form: NewVariableForm, nameExists: boolean): string | null {
  return (
    variableNameProblem(form.name.trim(), nameExists) ??
    (form.type === 'number' && form.value.trim() !== '' && !Number.isFinite(Number(form.value))
      ? 'The default must be a number.'
      : null) ??
    (form.type === 'url' && form.value.trim() !== '' && !Validators.isValidUrl(form.value.trim())
      ? 'The default must be a valid URL.'
      : null) ??
    (form.type === 'enum' && readItems(form.items).length === 0
      ? 'A choice variable needs at least one option.'
      : null)
  );
}

function definitionFromForm(form: NewVariableForm): VariableDefinition {
  const base: { required: boolean } = { required: form.required };
  switch (form.type) {
    case 'string':
    case 'url':
    case 'data_image_url':
      return { ...base, type: form.type, default: form.value };
    case 'number':
      return { ...base, type: 'number', default: Number(form.value.trim() || 0) };
    case 'boolean':
      return { ...base, type: 'boolean', default: false };
    case 'enum': {
      const options = readItems(form.items);
      const first = options[0];
      return first === undefined
        ? { ...base, type: 'enum', enum: [] }
        : { ...base, type: 'enum', enum: options, default: first };
    }
    case 'array':
      return { ...base, type: 'array', default: readItems(form.items) };
    case 'list':
      // Children are added inside the list's own card once it exists, so a new
      // list is an empty container.
      return { ...base, type: 'list', vars: {} };
  }
}

interface VariableDraftFieldsProps {
  form: NewVariableForm;
  /** Types the picker offers — a list's child form leaves out `list` itself. */
  types: VariableType[];
  onChange: (next: NewVariableForm) => void;
}

/**
 * The name, type and default of a variable being added. Shared by the top-level
 * add form and the form a list opens for one of its children, so a child is
 * described by the same fields as any other variable.
 */
function VariableDraftFields({ form, types, onChange }: VariableDraftFieldsProps) {
  const update = (part: Partial<NewVariableForm>) => onChange({ ...form, ...part });

  return (
    <>
      <input
        autoFocus
        className="field-input px-2 py-1 font-mono text-xs"
        placeholder="name"
        value={form.name}
        onChange={(e) => update({ name: e.target.value })}
      />
      <select
        title="Variable type"
        aria-label="New variable type"
        className="field-input px-2 py-1 text-xs"
        value={form.type}
        onChange={(e) => {
          const defaultVal = e.target.value === 'data_image_url' ? BLOGFORGER_QR_DATA_URL : '';

          update({ type: e.target.value as VariableType, items: '', value: defaultVal });
        }}
      >
        {types.map((type) => (
          <option key={type} value={type}>
            {VARIABLE_TYPE_LABELS[type]}
          </option>
        ))}
      </select>
      {(form.type === 'enum' || form.type === 'array') && (
        <input
          className="field-input px-2 py-1 text-xs"
          placeholder={
            form.type === 'enum' ? 'Choices, comma separated' : 'Items, comma separated'
          }
          value={form.items}
          onChange={(e) => update({ items: e.target.value })}
        />
      )}
      {(form.type === 'string' || form.type === 'number' || form.type === 'url') && (
        <input
          type={form.type === 'number' ? 'number' : 'text'}
          className="field-input px-2 py-1 text-base md:text-xs"
          placeholder="Default value (optional)"
          value={form.value}
          onChange={(e) => update({ value: e.target.value })}
        />
      )}

      {form.type === 'data_image_url' && (
        <textarea
          placeholder="Default value (optional)"
          value={form.value}
          className="field-input px-2 py-1 text-base md:text-xs"
          onChange={(e) => update({ value: e.target.value })}
        />
      )}
    </>
  );
}

interface EmailVariablesEditorProps {
  /** Definitions currently stored on the email (server truth). */
  rawDefinitions: unknown;
  /** Persists the complete record — the endpoint replaces it wholesale. */
  onSave: (next: VariableDefinitions) => Promise<unknown>;
}

/**
 * Defines the Mustache variables an email can use. Every edit is written
 * through in one PUT of the whole record, so `working` is a local mirror:
 * it keeps rapid consecutive edits from clobbering each other while a
 * request is still in flight, and adopts the server value once it lands.
 */
export function EmailVariablesEditor({ rawDefinitions, onSave }: EmailVariablesEditorProps) {
  const definitions = parseVariableDefinitions(rawDefinitions);

  const [working, setWorking] = useState<VariableDefinitions>(definitions);
  const [syncedFrom, setSyncedFrom] = useState(() => definitionsSnapshot(definitions));
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<NewVariableForm>(EMPTY_VARIABLE_FORM);
  /** The child form a list has open, and which list opened it. */
  const [childDraft, setChildDraft] = useState<{ parent: string; form: NewVariableForm } | null>(
    null,
  );
  /** Bumped whenever stored definitions replace the local ones, to re-seed inputs. */
  const [generation, setGeneration] = useState(0);

  // Adopt the stored value whenever the query hands back a different one, but
  // leave an echo of our own save (and anything still queued locally) alone.
  const stored = definitionsSnapshot(definitions);
  if (syncedFrom !== stored) {
    setSyncedFrom(stored);
    if (stored !== definitionsSnapshot(working)) {
      setWorking(definitions);
      setStatus('idle');
      setError(null);
      setGeneration((prev) => prev + 1);
    }
  }

  const busy = status === 'saving';
  const names = Object.keys(working).sort();

  const apply = (next: VariableDefinitions) => {
    setWorking(next);
    const problem = definitionsProblem(next);
    if (problem) {
      // Keep the edit on screen but out of the API — the record must stay valid.
      setStatus('error');
      setError(problem);
      return;
    }
    setStatus('saving');
    setError(null);
    void onSave(next).then(
      () => setStatus('saved'),
      (cause: unknown) => {
        setStatus('error');
        setError(variablesErrorMessage(cause));
      },
    );
  };

  const patch = (name: string, definition: VariableDefinition) =>
    apply({ ...working, [name]: definition });

  /** Writes one child of a list. A list holds no list, so a nested one is refused. */
  const patchChild = (parentName: string, childName: string, child: VariableDefinition) => {
    const parent = working[parentName];
    if (!parent || parent.type !== 'list' || child.type === 'list') return;
    patch(parentName, withListVars(parent, { ...parent.vars, [childName]: child }));
  };

  /** Drops one child, rewriting the list's default from the children that remain. */
  const removeChild = (parentName: string, childName: string) => {
    const parent = working[parentName];
    if (!parent || parent.type !== 'list' || !parent.vars) return;
    if (
      !confirm(`Remove the variable "${childName}" from "${parentName}"? The list's items lose it.`)
    ) {
      return;
    }
    const vars: ListVars = { ...parent.vars };
    delete vars[childName];
    patch(parentName, withListVars(parent, vars));
  };

  const remove = (name: string) => {
    if (!confirm(`Remove the variable "${name}"? Blocks that reference it will lose its value.`)) {
      return;
    }
    const next: VariableDefinitions = {};
    for (const [key, value] of Object.entries(working)) {
      if (key !== name) next[key] = value;
    }
    apply(next);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = form.name.trim();
    const problem = draftProblem(form, Object.prototype.hasOwnProperty.call(working, name));
    if (problem) {
      setStatus('error');
      setError(problem);
      return;
    }
    setForm(EMPTY_VARIABLE_FORM);
    setAdding(false);
    apply({ ...working, [name]: definitionFromForm(form) });
  };

  /** The child form a list opens: its parent is the list whose card holds it. */
  const submitChild = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!childDraft) return;
    const { parent: parentName, form: draft } = childDraft;
    const parent = working[parentName];
    if (!parent || parent.type !== 'list') return;

    const childName = draft.name.trim();
    const problem = draftProblem(
      draft,
      Object.prototype.hasOwnProperty.call(parent.vars ?? {}, childName),
    );
    if (problem) {
      setStatus('error');
      setError(problem);
      return;
    }
    setChildDraft(null);
    patchChild(parentName, childName, definitionFromForm(draft));
  };

  const cancelAdd = () => {
    setForm(EMPTY_VARIABLE_FORM);
    setAdding(false);
    setStatus('idle');
    setError(null);
  };

  /**
   * The per-type editor for one definition's default. `write` is how an edit
   * travels: the panel patches the record for a top-level variable, while a
   * list's child patches the parent's `vars` instead.
   */
  const defaultControl = (
    name: string,
    definition: VariableDefinition,
    write: (next: VariableDefinition) => void = (next) => patch(name, next),
  ) => {
    switch (definition.type) {
      case 'string':

      case 'url':
        return (
          <input
            key={definition.type}
            className="field-input px-2 py-1 text-base md:text-xs"
            defaultValue={definition.default ?? ''}
            placeholder="Default value (optional)"
            disabled={busy}
            onBlur={(e) => {
              const value = e.target.value;
              write(value === '' ? withoutDefault(definition) : { ...definition, default: value });
            }}
          />
        );

      case 'data_image_url':
        return (
          <textarea
            key={definition.type}
            className="field-input px-2 py-1 text-base md:text-xs"
            defaultValue={definition.default ?? ''}
            placeholder="Default value (optional)"
            disabled={busy}
            onBlur={(e) => {
              const value = e.target.value;
              write(value === '' ? withoutDefault(definition) : { ...definition, default: value });
            }}
          />
        );

      case 'number':
        return (
          <input
            key={definition.type}
            type="number"
            className="field-input px-2 py-1 text-xs"
            defaultValue={definition.default ?? ''}
            placeholder="Default value (optional)"
            disabled={busy}
            onBlur={(e) => {
              const raw = e.target.value.trim();
              if (raw === '') {
                write(withoutDefault(definition));
                return;
              }
              const value = Number(raw);
              if (!Number.isFinite(value)) {
                setStatus('error');
                setError(`The default for "${name}" must be a number.`);
                return;
              }
              write({ ...definition, default: value });
            }}
          />
        );
      case 'boolean':
        return (
          <label className="text-muted flex cursor-pointer items-center gap-1.5 text-[11px]">
            <input
              type="checkbox"
              checked={definition.default ?? false}
              disabled={busy}
              onChange={(e) => write({ ...definition, default: e.target.checked })}
            />
            Default to yes
          </label>
        );
      case 'enum':
        return (
          <div className="space-y-1.5">
            <input
              className="field-input px-2 py-1 text-xs"
              defaultValue={definition.enum.join(', ')}
              placeholder="Choices, comma separated"
              disabled={busy}
              onBlur={(e) => write(withEnumOptions(definition, readItems(e.target.value)))}
            />
            <select
              className="field-input px-2 py-1 text-xs"
              aria-label={`Default for ${name}`}
              value={definition.default ?? ''}
              disabled={busy || definition.enum.length === 0}
              onChange={(e) => write({ ...definition, default: e.target.value })}
            >
              {definition.default === undefined && <option value="">No default</option>}
              {definition.enum.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );
      case 'array':
        return (
          <input
            className="field-input px-2 py-1 text-xs"
            defaultValue={(definition.default ?? []).join(', ')}
            placeholder="Default items, comma separated (optional)"
            disabled={busy}
            onBlur={(e) => {
              const items = readItems(e.target.value);
              write(
                items.length === 0 ? withoutDefault(definition) : { ...definition, default: items },
              );
            }}
          />
        );
      case 'list': {
        // A list is edited through its children: what they are, and what each
        // one defaults to. The list's own default follows them (see
        // `listDefaultFromVars`), so there is nothing here to type as an item.
        const vars = definition.vars ?? {};
        const childNames = Object.keys(vars).sort();
        const draft = childDraft?.parent === name ? childDraft.form : null;

        return (
          <div className="space-y-1.5">
            {childNames.length === 0 && draft === null && (
              <p className="text-muted text-[11px]">
                A list holds its own variables — add one for each value an item carries.
              </p>
            )}

            {childNames.map((childName) => (
              <div
                key={childName}
                className="border-line bg-card-2 space-y-1.5 rounded-lg border p-2"
              >
                <div className="flex items-center gap-1.5">
                  <code className="text-ink min-w-0 flex-1 truncate font-mono text-[11px]">
                    {childName}
                  </code>
                  <select
                    title="Child variable type"
                    aria-label={`Type of ${childName} in ${name}`}
                    className="field-input w-auto px-2 py-0.5 text-xs"
                    value={vars[childName]!.type}
                    disabled={busy}
                    onChange={(e) =>
                      patchChild(
                        name,
                        childName,
                        reseedDefinition(vars[childName]!, e.target.value as VariableType),
                      )
                    }
                  >
                    {CHILD_VARIABLE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {VARIABLE_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    title={`Remove ${childName}`}
                    aria-label={`Remove ${childName}`}
                    className="text-muted hover:text-danger flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors"
                    onClick={() => removeChild(name, childName)}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>

                {defaultControl(childName, vars[childName]!, (next) =>
                  patchChild(name, childName, next),
                )}
              </div>
            ))}

            {draft !== null ? (
              <form
                onSubmit={submitChild}
                className="border-line bg-card-2 space-y-1.5 rounded-lg border p-2"
              >
                <VariableDraftFields
                  form={draft}
                  types={CHILD_VARIABLE_TYPES}
                  onChange={(next) => setChildDraft({ parent: name, form: next })}
                />
                <div className="flex items-center gap-2">
                  <button type="submit" className="btn-primary px-3 py-1 text-xs">
                    Add variable
                  </button>
                  <button
                    type="button"
                    className="btn-ghost px-3 py-1 text-xs"
                    onClick={() => setChildDraft(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                type="button"
                className="btn-secondary w-full py-1.5 text-xs"
                disabled={busy}
                onClick={() => setChildDraft({ parent: name, form: EMPTY_VARIABLE_FORM })}
              >
                <Plus size={13} strokeWidth={2.6} />
                Add variable
              </button>
            )}
          </div>
        );
      }
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {names.length === 0 && !adding && (
        <p className="text-muted text-xs">
          No variables yet — define one to reuse a value across blocks.
        </p>
      )}

      {names.length > 0 && (
        <ul className="space-y-2">
          {names.map((name) => {
            const definition = working[name];
            if (!definition) return null;
            return (
              <li
                key={`${name}:${generation}`}
                className="border-line bg-card space-y-2 rounded-lg border p-2.5"
              >
                <div className="flex items-center gap-1.5">
                  <code className="text-ink min-w-0 flex-1 truncate font-mono text-xs">{`{{${name}}}`}</code>
                  <select
                    title="Variable type"
                    aria-label={`Type of ${name}`}
                    className="field-input w-auto px-2 py-0.5 text-xs"
                    value={definition.type}
                    disabled={busy}
                    onChange={(e) =>
                      patch(name, reseedDefinition(definition, e.target.value as VariableType))
                    }
                  >
                    {VARIABLE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {VARIABLE_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    title={`Remove ${name}`}
                    aria-label={`Remove ${name}`}
                    className="text-muted hover:text-danger flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors"
                    onClick={() => remove(name)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>

                <div>
                  {defaultControl(name, definition)}{' '}
                  <span className="text-muted text-xs leading-[100%]">
                    For edit render purposes only
                  </span>
                </div>

                <label className="text-muted flex cursor-pointer items-center gap-1.5 text-[11px]">
                  <input
                    type="checkbox"
                    checked={definition.required}
                    disabled={busy}
                    onChange={(e) => patch(name, { ...definition, required: e.target.checked })}
                  />
                  Required — a value must be provided
                </label>

                <input
                  className="field-input px-2 py-1 text-xs"
                  defaultValue={definition.description ?? ''}
                  placeholder="Description (optional)"
                  disabled={busy}
                  onBlur={(e) => {
                    const description = e.target.value.trim();
                    patch(
                      name,
                      description === ''
                        ? withoutDescription(definition)
                        : { ...definition, description },
                    );
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}

      {adding ? (
        <form onSubmit={submit} className="border-line bg-card-2 space-y-2 rounded-lg border p-2.5">
          <VariableDraftFields form={form} types={VARIABLE_TYPES} onChange={setForm} />
          <label className="text-muted flex cursor-pointer items-center gap-1.5 text-[11px]">
            <input
              type="checkbox"
              checked={form.required}
              onChange={(e) => setForm((prev) => ({ ...prev, required: e.target.checked }))}
            />
            Required — a value must be provided
          </label>
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary px-3 py-1 text-xs">
              Add variable
            </button>
            <button type="button" className="btn-ghost px-3 py-1 text-xs" onClick={cancelAdd}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="btn-secondary w-full py-1.5 text-xs"
          onClick={() => setAdding(true)}
        >
          <Plus size={13} strokeWidth={2.6} />
          Add variable
        </button>
      )}

      {status !== 'idle' && (
        <p
          className={`text-xs ${
            status === 'error' ? 'text-danger' : status === 'saved' ? 'text-ok' : 'text-muted'
          }`}
        >
          {status === 'saving'
            ? 'Saving…'
            : (error ?? (status === 'saved' ? 'Variables saved.' : 'Not saved.'))}
        </p>
      )}
    </div>
  );
}
